import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Dropdown from '../ui/Dropdown';
import MobileMenuCard from './MobileMenuCard';

interface FloatingDockProps {
  className?: string;
}

interface DockButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  to?: string;
  href?: string;
  isActive?: boolean;
  icon?: string;
  className?: string;
  ref?: React.RefObject<HTMLButtonElement | null>;
}

interface SocialLink {
  label: string;
  href: string;
  icon?: string;
}

const DockButton: React.FC<DockButtonProps> = ({ 
  children, 
  onClick, 
  to, 
  href,
  isActive = false, 
  icon, 
  className = '',
  ref
}) => {
  const baseClasses = `dock-link ${isActive ? 'dock-link-active' : ''} ${className}`;
  
  // Handle external links
  if (href) {
    return (
      <a 
        href={href} 
        className={baseClasses}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        <span>{children}</span>
        {icon && <img src={icon} alt="" style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
      </a>
    );
  }
  
  // Handle internal links
  if (to) {
    return (
      <Link to={to} className={baseClasses}>
        {icon && <span style={{ marginRight: '4px' }}>{icon}</span>}
        {children}
      </Link>
    );
  }
  
  // Handle buttons
  return (
    <button onClick={onClick} className={baseClasses} ref={ref}>
      {icon && <span style={{ marginRight: '4px' }}>{icon}</span>}
      {children}
    </button>
  );
};

const FloatingDock: React.FC<FloatingDockProps> = ({ className = '' }) => {
  const location = useLocation();
  const [isFollowDropdownOpen, setIsFollowDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { 
      href: 'https://apps.apple.com/ca/app/lut-rolls-pro-photo-filters/id6746622304', 
      label: 'Download',
      icon: '/assets/apple.svg'
    }
  ];

  const socialLinks: SocialLink[] = [
    { label: 'Threads', href: 'https://www.threads.com/@thelutrolls' },
    { label: 'Instagram', href: 'https://www.instagram.com/thelutrolls/' }
  ];

  // Mobile menu items
  const mobileMenuItems = [
    { 
      label: 'About', 
      to: '/about'
    },
    { 
      label: 'Contact', 
      to: '/contact'
    },
    { 
      label: 'Privacy',
      to: '/privacy'
    },
    { 
      label: 'Threads',
      href: 'https://www.threads.com/@thelutrolls'
    },
    { 
      label: 'Instagram',
      href: 'https://www.instagram.com/thelutrolls/'
    }
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const toggleFollowDropdown = () => {
    setIsFollowDropdownOpen(!isFollowDropdownOpen);
  };

  const closeFollowDropdown = () => {
    setIsFollowDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav 
      className={`floating-dock ${className}`}
    >
      <motion.ul 
        className="dock-links"
      >
        {navItems
          .filter(item => {
            // Hide About button on mobile
            if (isMobile && item.label === 'About') {
              return false;
            }
            return true;
          })
          .map((item, index) => (
          <motion.li 
            key={item.path || item.href || index}
            className={item.label === 'Contact' ? 'dock-contact-item' : ''}
          >
            <DockButton 
              to={item.path}
              href={item.href}
              icon={item.icon}
              isActive={item.path ? isActive(item.path) : false}
            >
              {item.label}
            </DockButton>
          </motion.li>
        ))}
        
        {/* Follow dropdown (desktop only) or More button (mobile only) */}
        {isMobile ? (
          <motion.li>
            <DockButton 
              onClick={toggleMobileMenu}
              className={isMobileMenuOpen ? 'dock-link-active' : ''}
              ref={moreButtonRef}
            >
              More...
            </DockButton>
          </motion.li>
        ) : (
          <Dropdown
            trigger={
              <DockButton 
                onClick={toggleFollowDropdown}
                className={isFollowDropdownOpen ? 'dock-link-active' : ''}
              >
                Follow
              </DockButton>
            }
            items={socialLinks}
            isOpen={isFollowDropdownOpen}
            onToggle={toggleFollowDropdown}
            onClose={closeFollowDropdown}
          />
        )}
      </motion.ul>
      
      {/* Mobile Menu Card */}
      <MobileMenuCard
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        menuItems={mobileMenuItems}
        toggleButtonRef={moreButtonRef}
      />
    </motion.nav>
  );
};

export default FloatingDock; 